import React from 'react'
import { useLocation } from '@reach/router'
import isEmpty from 'lodash/isEmpty'
import type { CheckoutAttributionRequest } from '@sidegigllc/api/client'
import { ExternalGigproLinks } from '../constants'

const APP_URL = ExternalGigproLinks.login
const STORAGE_KEY = 'qwick_utm'

// Longest value the backend attribution fields accept (utm_term/utm_content).
// Omit anything longer so a stray oversized param can't break checkout start.
const MAX_PARAM_LENGTH = 255

const isAppUrl = (url: string): boolean => url.startsWith(APP_URL)

const readSearchParams = (search: string): Record<string, string> => {
  const params: Record<string, string> = {}
  new URLSearchParams(search).forEach((value, key) => {
    if (value.length <= MAX_PARAM_LENGTH) params[key] = value
  })
  return params
}

/**
 * Persist any query params present in the URL to sessionStorage so attribution
 * survives internal navigation (Gatsby <Link> drops the query string) and
 * refreshes. Last-touch: a later landing with params overwrites; internal nav
 * (no query string) leaves the stored value untouched. Call on every route.
 */
export function captureSearchParams(search: string): void {
  if (typeof window === 'undefined') return
  const params = readSearchParams(search)
  if (isEmpty(params)) return
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params))
  } catch {
    // sessionStorage unavailable (private mode, etc.) — nothing to persist.
  }
}

/** Parsed params from sessionStorage; {} when none or unavailable. */
function readStoredUtm(): Record<string, string> {
  if (typeof window === 'undefined') return {}
  try {
    return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) ?? '{}')
  } catch {
    return {}
  }
}

/**
 * Stored params shaped for the checkout `attribution` field; undefined when
 * none. Forwards every stored param so new backend-recognized keys need no
 * change here — the cast covers params not yet in the generated type. The
 * backend ignores keys it doesn't recognize.
 */
export function getStoredAttribution(): CheckoutAttributionRequest | undefined {
  const stored = readStoredUtm()
  return isEmpty(stored) ? undefined : (stored as CheckoutAttributionRequest)
}

/**
 * Resolve an app URL with stored params appended. Starts from the bare URL
 * (matching SSR output) and upgrades after mount so hydration never sees a
 * mismatched href; re-resolves on navigation in case fresh params landed.
 * No-op for non-app URLs.
 */
export function useForwardedHref(url?: string): string | undefined {
  const { search, pathname } = useLocation()
  const [resolved, setResolved] = React.useState(url)
  React.useEffect(() => {
    // Capture before reading so a fresh landing is reflected this tick,
    // independent of gatsby-browser onRouteUpdate ordering.
    captureSearchParams(search)
    if (!url || !isAppUrl(url)) {
      setResolved(url)
      return
    }
    const query = new URLSearchParams(readStoredUtm()).toString()
    setResolved(query ? `${url}${url.includes('?') ? '&' : '?'}${query}` : url)
  }, [url, search, pathname])
  return resolved
}

export function useAppUrl(url: string = APP_URL): string {
  return useForwardedHref(url) ?? url
}

/**
 * Stored params as a `URLSearchParams`, hydration-safe: empty on first render
 * (matches SSR), populated after mount. For call sites that thread a params
 * object into inline hrefs (e.g. the header login/signup buttons).
 */
export function useStoredUtmParams(): URLSearchParams {
  const { search, pathname } = useLocation()
  const [params, setParams] = React.useState(() => new URLSearchParams())
  React.useEffect(() => {
    captureSearchParams(search)
    setParams(new URLSearchParams(readStoredUtm()))
  }, [search, pathname])
  return params
}
